/**
 * DAGStrip — thin horizontal strip showing node pills in topological order.
 *
 * Only visible in Tool view. Replaces the full DAG with a compact navigable strip.
 * Includes [← DAG] back button, status icons per node, auto-scroll to selected.
 */

import React, { useRef, useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';
import type { ModelDefinition, ModelRun, RunStatus } from '../../types/modeling';
import { getNodeTypeColor } from '../../types/modeling';

interface DAGStripProps {
  model: ModelDefinition;
  topologicalOrder: string[];
  selectedNodeId: string | null;
  lastRun: ModelRun | null;
  activeRunProgress: ModelRun | null;
  onNodeSelect: (nodeId: string) => void;
  onBackToDAG: () => void;
}

export default function DAGStrip({
  model,
  topologicalOrder,
  selectedNodeId,
  lastRun,
  activeRunProgress,
  onNodeSelect,
  onBackToDAG,
}: DAGStripProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const selectedRef = useRef<HTMLButtonElement>(null);

  // Build node lookup
  const nodeMap = new Map(model.nodes.map((n) => [n.node_id, n]));

  // Build run status lookup
  const runStatusMap: Record<string, RunStatus> = {};
  const run = activeRunProgress || lastRun;
  if (run?.node_results) {
    run.node_results.forEach((nr) => {
      runStatusMap[nr.node_id] = nr.status;
    });
  }

  // Auto-scroll to selected node
  useEffect(() => {
    if (selectedRef.current) {
      selectedRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }, [selectedNodeId]);

  return (
    <div className="flex items-center h-14 px-2 bg-bg-warm border-b border-border-default flex-shrink-0 gap-2">
      {/* Back to DAG button */}
      <button
        onClick={onBackToDAG}
        className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-fg-secondary bg-surface border border-border-default rounded-md hover:bg-bg-warm transition-colors flex-shrink-0"
      >
        <ArrowLeft className="w-3.5 h-3.5" />
        DAG
      </button>

      {/* Scrollable node pills */}
      <div
        ref={scrollRef}
        className="flex items-center gap-1.5 overflow-x-auto flex-1 py-1 scrollbar-thin"
      >
        {topologicalOrder.map((nodeId, idx) => {
          const node = nodeMap.get(nodeId);
          if (!node) return null;

          const isSelected = nodeId === selectedNodeId;
          const color = getNodeTypeColor(node.node_type);
          const status = runStatusMap[nodeId];

          return (
            <React.Fragment key={nodeId}>
              {idx > 0 && (
                <div className="w-3 h-px bg-border-default flex-shrink-0" />
              )}
              <button
                ref={isSelected ? selectedRef : undefined}
                onClick={() => onNodeSelect(nodeId)}
                className={`
                  flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium
                  transition-all duration-150 flex-shrink-0 whitespace-nowrap
                  ${isSelected
                    ? 'text-white shadow-sm'
                    : 'text-fg-secondary bg-surface border border-border-default hover:border-border-default'
                  }
                `}
                style={isSelected ? { backgroundColor: color } : undefined}
                title={node.name}
              >
                {/* Status dot */}
                <div
                  className={`w-2 h-2 rounded-full flex-shrink-0 ${
                    status === 'completed' ? 'bg-success'
                    : status === 'running' ? 'bg-accent animate-pulse'
                    : status === 'failed' ? 'bg-danger'
                    : isSelected ? 'bg-surface/60' : 'bg-border-default'
                  }`}
                />
                {node.name.length > 20 ? node.name.slice(0, 18) + '...' : node.name}
              </button>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}
